import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isIOSPath, standardPath, useWebPath } from './lib/web-presentation';
import { AppShell } from './components/AppShell';
import { Brand } from './components/Brand';
import { Button } from './components/Button';
import { LoginScreen } from './components/LoginScreen';
import { ThemeToggle } from './components/ThemeToggle';
import { AccountPortal } from './components/AccountPortal';
import { FirstUseSetup } from './components/FirstUseSetup';
import { AcquisitionPortal, CheckoutCompletion, EmailReminder, VerifyEmail } from './components/AcquisitionPortal';
import { CommercialAdmin } from './components/CommercialAdmin';
import { OperationsConsole } from './components/OperationsConsole';
import { ApiError, AmoyeApi, type AccountSession, type AccountSubject, type IdentityRail, type ProductTrack,
  type ResolvedIdentityContext } from './lib/api';
import { clearAccountSession, readAccountSession, storeAccountSession } from './lib/account-session-storage';
import { navigateProduct, productFromPath } from './lib/product-routes';
import { personaDefinitions } from './personas';
import type { PersonaId } from './types/persona';

const personaByTrack: Record<ProductTrack, PersonaId> = { A: 'practitioner', B1: 'business', B2: 'individual' };
const trackByPersona: Record<PersonaId, ProductTrack> = { practitioner: 'A', business: 'B1', individual: 'B2' };

export default function App() {
  const pathname = useWebPath();
  const appPath = standardPath(pathname);
  const ios = isIOSPath(pathname);
  useLayoutEffect(() => {
    document.documentElement.dataset.presentation = ios ? 'ios' : 'standard';
    return () => { delete document.documentElement.dataset.presentation; };
  }, [ios]);
  useEffect(() => {
    if (!ios) return;
    const viewport = window.visualViewport;
    const update = () => {
      const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, select');
      const keyboard = editing && viewport && viewport.height < window.innerHeight - 100;
      document.documentElement.toggleAttribute('data-ios-keyboard', Boolean(keyboard));
      document.documentElement.style.setProperty('--ios-visible-height', `${viewport?.height ?? window.innerHeight}px`);
    };
    update();
    viewport?.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      document.documentElement.removeAttribute('data-ios-keyboard');
      document.documentElement.style.removeProperty('--ios-visible-height');
    };
  }, [ios]);
  const [restoreCandidate] = useState(() => productFromPath() || isIOSPath() && standardPath() === '/' ? readAccountSession() : undefined);
  const [session, setSession] = useState<AccountSession | null>(null);
  const [sessionNotice, setSessionNotice] = useState('');
  const [authenticationState, setAuthenticationState] = useState<'signed-out' | 'restoring' | 'restore-failed' | 'signed-in'>(
    () => restoreCandidate ? 'restoring' : 'signed-out');
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const [contexts, setContexts] = useState<ResolvedIdentityContext[]>([]);
  const [contextState, setContextState] = useState<'loading' | 'ready' | 'legacy'>('legacy');
  const [activePersona, setActivePersona] = useState<PersonaId | null>(() => productFromPath() ?? null);
  const tokenRef = useRef<string | undefined>(restoreCandidate?.accessToken);
  const contextIdsRef = useRef<Partial<Record<IdentityRail, string>>>({});
  const resolvedContextsRef = useRef<Partial<Record<IdentityRail, ResolvedIdentityContext>>>({});
  const availableContextsRef = useRef<ResolvedIdentityContext[]>([]);
  const authenticationRequiredRef = useRef<(error: ApiError) => void>(() => undefined);
  if (session) tokenRef.current = session.access_token;
  const api = useMemo(() => new AmoyeApi({
    getToken: () => tokenRef.current,
    getContextId: (rail) => contextIdsRef.current[rail],
    getResolvedContext: (rail) => resolvedContextsRef.current[rail],
    getResolvedContexts: () => availableContextsRef.current,
    onIdentityContextsRefreshed: (refreshed) => {
      availableContextsRef.current = [...refreshed];
      setContexts([...refreshed]);
      setContextState('ready');
    },
    onAuthenticationRequired: (error) => authenticationRequiredRef.current(error),
  }), []);

  const endSession = (notice = '') => {
    api.clearIdentityContextSelection(); clearAccountSession(); tokenRef.current = undefined;
    setSession(null); setAuthenticationState('signed-out'); setSessionNotice(notice); contextIdsRef.current = {};
    resolvedContextsRef.current = {}; availableContextsRef.current = [];
  };
  authenticationRequiredRef.current = () => endSession(
    'Your sign-in expired. Sign in again to continue. Documents that had not finished uploading were not added.');

  useEffect(() => {
    if (!restoreCandidate) return;
    const controller = new AbortController();
    setAuthenticationState('restoring');
    tokenRef.current = restoreCandidate.accessToken;
    void api.currentSession(restoreCandidate.accessToken, controller.signal).then((current) => {
      const expiresIn = Math.max(1, Math.ceil((restoreCandidate.expiresAt - Date.now()) / 1000));
      setSession({ ...current, access_token: restoreCandidate.accessToken, token_type: 'Bearer', expires_in: expiresIn });
      setAuthenticationState('signed-in');
    }).catch((error: unknown) => {
      if (controller.signal.aborted) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearAccountSession(); tokenRef.current = undefined; setAuthenticationState('signed-out'); return;
      }
      setAuthenticationState('restore-failed');
    });
    return () => controller.abort();
  }, [api, restoreAttempt, restoreCandidate]);

  useEffect(() => {
    if (!session) {
      setContexts([]); setContextState('legacy'); setActivePersona(null); contextIdsRef.current = {};
      resolvedContextsRef.current = {};
      availableContextsRef.current = [];
      return;
    }
    const controller = new AbortController();
    setContextState('loading');
    const requestedFromUrl = productFromPath();
    setActivePersona(requestedFromUrl ?? session.product);
    void api.identityContexts(controller.signal).then((response) => {
      if (!Array.isArray(response.contexts) || !response.contexts.every(isIdentityContext)) throw new Error('invalid identity context response');
      // The authenticated product remains available even before its first
      // taxpayer context exists. Other products are discovered from their
      // server-authorized contexts.
      const availablePersonas = personaDefinitions.filter((definition) => definition.id === session.product
        || response.contexts.some((context) => personaByTrack[context.scope.productTrack] === definition.id));
      const selected = requestedFromUrl && availablePersonas.some(({ id }) => id === requestedFromUrl)
        ? requestedFromUrl : availablePersonas.some(({ id }) => id === session.product)
          ? session.product : availablePersonas[0]?.id ?? null;
      availableContextsRef.current = response.contexts;
      setContexts(response.contexts); setActivePersona(selected); setContextState('ready');
      if (selected && productFromPath() !== selected) navigateProduct(selected, undefined, true);
    }).catch((error: unknown) => {
      if (controller.signal.aborted) return;
      // Older/local deployments may not expose identity contexts yet. The
      // server-selected session product remains the compatibility boundary.
      availableContextsRef.current = [];
      setContexts([]); setActivePersona(session.product); setContextState('legacy');
      if (productFromPath() !== session.product) navigateProduct(session.product, undefined, true);
      void error;
    });
    return () => controller.abort();
  }, [api, session]);

  useEffect(() => {
    if (!session) return;
    const timeout = window.setTimeout(() => authenticationRequiredRef.current(new ApiError(401, { error: {
      code: 'session_expired', message: 'Your sign-in expired.', retryable: false,
    } })), Math.max(1, session.expires_in) * 1000);
    return () => window.clearTimeout(timeout);
  }, [session?.access_token, session?.expires_in]);

  useEffect(() => {
    const syncProductFromUrl = () => {
      const requested = productFromPath();
      if (requested) setActivePersona(requested);
    };
    window.addEventListener('popstate', syncProductFromUrl);
    return () => window.removeEventListener('popstate', syncProductFromUrl);
  }, []);

  const acceptSession = (authenticated: AccountSession) => {
    api.clearIdentityContextSelection();
    api.resetAuthenticationFailure();
    tokenRef.current = authenticated.access_token;
    storeAccountSession(authenticated);
    setSessionNotice(''); setContextState('loading'); setSession(authenticated); setAuthenticationState('signed-in');
  };
  const signOut = () => {
    if(session?.access_token.startsWith('cookie:'))void api.logoutAccount(session.product).finally(()=>endSession());
    else endSession();
  };

  if(appPath.startsWith('/account/'))return <AccountPortal/>;

  if (!session && authenticationState === 'restoring') {
    return <SessionOpening product={productFromPath() ?? restoreCandidate?.product ?? 'practitioner'}
      message="Confirming that your saved session is still valid…" />;
  }
  if (!session && authenticationState === 'restore-failed') {
    const abandon = () => { clearAccountSession(); tokenRef.current = undefined; setAuthenticationState('signed-out'); };
    return <main className="login-screen">
      <div className="login-screen__chrome"><Brand surface={surfaceName(productFromPath() ?? restoreCandidate?.product ?? 'practitioner')} /><ThemeToggle /></div>
      <section aria-live="polite" className="login-card" role="alert">
        <p className="eyebrow">ACCOUNT ACCESS</p><h1>We could not reopen your session</h1>
        <p>Your saved sign-in has not been discarded. Check that the Amoye service is running, then try again.</p>
        <div className="am-cluster"><Button onClick={() => setRestoreAttempt((value) => value + 1)}>Try again</Button>
          <Button onClick={abandon} tone="secondary">Sign in again</Button></div>
      </section>
    </main>;
  }

  if (!session && appPath === '/admin/commercial') return <CommercialAdmin api={api} />;
  if (!session && appPath === '/admin/operations') return <OperationsConsole api={api} />;
  if (!session && appPath === '/account-recovery') return <EmailReminder api={api} />;
  if (!session && ['/get-started/checkout','/get-started/payment-complete'].includes(appPath)) {
    return <CheckoutCompletion api={api} />;
  }
  if (!session && appPath === '/get-started/verify-email') return <VerifyEmail api={api} />;
  if (!session && appPath.endsWith('/get-started')) {
    return <AcquisitionPortal api={api} product={productFromPath() ?? 'business'} />;
  }

  if (!session) {
    return <LoginScreen api={api} initialProduct={productFromPath() ?? (ios ? 'individual' : 'practitioner')}
      notice={sessionNotice} onAuthenticated={acceptSession} />;
  }

  if (contextState === 'loading') {
    const requestedProduct = productFromPath() ?? session.product;
    return <SessionOpening product={requestedProduct}
      message="Checking the products, taxpayers and actions available to this account…" />;
  }

  const selectedPersona = activePersona ?? session.product;
  const activeProduct = personaDefinitions.find(({ id }) => id === selectedPersona);
  if (!activeProduct) {
    return null;
  }
  const availableProducts = contextState === 'ready'
    ? personaDefinitions.filter(({ id }) => id === session.product
      || contexts.some((context) => context.scope.productTrack === trackByPersona[id]))
    : [activeProduct];
  const railContexts = selectRailContexts(contexts, trackByPersona[activeProduct.id]);
  contextIdsRef.current = contextState === 'ready' ? {
    ...(railContexts.tax_return ? { tax_return: railContexts.tax_return.contextId } : {}),
    ...(railContexts.e_invoice ? { e_invoice: railContexts.e_invoice.contextId } : {}),
  } : {};
  resolvedContextsRef.current = contextState === 'ready' ? {
    ...(railContexts.tax_return ? { tax_return: railContexts.tax_return } : {}),
    ...(railContexts.e_invoice ? { e_invoice: railContexts.e_invoice } : {}),
  } : {};
  const selectedContext = railContexts.tax_return ?? railContexts.e_invoice;
  const subjects = contextState === 'ready'
    ? activeProduct.id === 'practitioner'
      ? uniqueTrackASubjects(contexts, session.subjects)
      : activeProduct.id === 'business'
        ? uniqueProductSubjects(contexts, session.subjects, 'B1')
      : selectedContext ? [subjectFromContexts(selectedContext,
        contexts.filter((context) => context.scope.productTrack === selectedContext.scope.productTrack
          && context.scope.taxSubjectId === selectedContext.scope.taxSubjectId), session.subjects)] : []
    : session.subjects;
  const Product = activeProduct.Workspace;
  const completeTaxSubjectSetup = (subject: import('./lib/api').CreatedTaxSubject) => {
    setContextState('loading');
    setSession((current) => current ? { ...current,
      subjects: [...current.subjects.filter((item) => item.id !== subject.id), { id: subject.id,
        display_name: subject.display_name, kind: subject.kind, tax_subject_kind: subject.tax_subject_kind,
        workspace_name: subject.workspace_name, ...(subject.tax_id_hint ? { tax_id_hint: subject.tax_id_hint } : {}),
        tax_identity_status: subject.tax_identity_status }],
    } : current);
  };
  const refreshIdentityContexts = () => {
    setContextState('loading');
    setSession((current) => current ? { ...current } : current);
  };

  const hasProductContext = contextState !== 'ready'
    || contexts.some((context) => context.scope.productTrack === trackByPersona[activeProduct.id]);
  // First use is product-scoped. A shared identity may already have a subject
  // in another product; that must not suppress setup for this empty surface.
  if (!hasProductContext) {
    return <AppShell activeProduct={activeProduct} availableProducts={[activeProduct]}
      secureAccount={session.access_token.startsWith('cookie:')}
      onProductChange={() => undefined}
      onSignOut={signOut}
      signedInAccount={session.account}>
      <FirstUseSetup api={api} product={activeProduct.id} onComplete={completeTaxSubjectSetup} />
    </AppShell>;
  }

  return (
    <AppShell
      secureAccount={session.access_token.startsWith('cookie:')}
      activeProduct={activeProduct}
      actingCapacity={selectedContext?.actingCapacity}
      availableProducts={availableProducts}
      onProductChange={(product) => { api.clearIdentityContextSelection(); setActivePersona(product); navigateProduct(product, undefined); }}
      onSignOut={signOut}
      signedInAccount={session.account}
    >
      <Product key={`${activeProduct.id}:${selectedContext?.contextId ?? 'legacy'}`} api={api} subjects={subjects}
        onTaxSubjectCreated={completeTaxSubjectSetup} onContextsChanged={refreshIdentityContexts} />
    </AppShell>
  );
}

function SessionOpening({ message, product }: { message: string; product: PersonaId }) {
  return <main className="login-screen">
    <div className="login-screen__chrome"><Brand surface={surfaceName(product)} /><ThemeToggle /></div>
    <section aria-live="polite" className="login-card" role="status">
      <p className="eyebrow">ACCOUNT ACCESS</p><h1>Opening your tax records</h1><p>{message}</p>
    </section>
  </main>;
}

function selectRailContexts(contexts: readonly ResolvedIdentityContext[], track: ProductTrack) {
  const matching = contexts.filter((context) => context.scope.productTrack === track);
  const primary = matching.find((context) => context.scope.rail === 'tax_return') ?? matching[0];
  const sameSubject = primary ? matching.filter((context) => context.scope.taxSubjectId === primary.scope.taxSubjectId) : [];
  return {
    tax_return: sameSubject.find((context) => context.scope.rail === 'tax_return'),
    e_invoice: sameSubject.find((context) => context.scope.rail === 'e_invoice'),
  };
}

function subjectFromContext(context: ResolvedIdentityContext, sessionSubjects: readonly AccountSubject[]): AccountSubject {
  const existing = sessionSubjects.find((subject) => subject.id === context.scope.taxSubjectId);
  return {
    id: context.scope.taxSubjectId,
    display_name: existing?.display_name ?? `Tax subject ${context.scope.taxSubjectId.slice(-8)}`,
    kind: context.scope.productTrack === 'A' ? 'client' : context.scope.productTrack === 'B1' ? 'business' : 'individual',
    ...(existing?.workspace_name ? { workspace_name: existing.workspace_name } : {}),
    ...(existing?.tax_id_hint ? { tax_id_hint: existing.tax_id_hint } : {}),
    ...(existing?.tax_identity_status ? { tax_identity_status: existing.tax_identity_status } : {}),
    ...(context.scope.taxSubjectKind ? { tax_subject_kind: context.scope.taxSubjectKind } : {}),
    engagement_id: context.scope.engagementId,
    context_id: context.contextId,
    context_ids: { [context.scope.rail]: context.contextId },
    resolved_contexts: [context],
  };
}

function subjectFromContexts(primary: ResolvedIdentityContext, contexts: readonly ResolvedIdentityContext[],
  sessionSubjects: readonly AccountSubject[]): AccountSubject {
  const subject = subjectFromContext(primary, sessionSubjects);
  return contexts.reduce<AccountSubject>((current, context) => ({ ...current,
    ...(context.scope.taxSubjectKind ? { tax_subject_kind: context.scope.taxSubjectKind } : {}),
    context_ids: { ...current.context_ids, [context.scope.rail]: current.context_ids?.[context.scope.rail]
      ?? context.contextId },
    resolved_contexts: [...(current.resolved_contexts ?? []).filter((item) => item.contextId !== context.contextId), context],
  }), subject);
}

function uniqueTrackASubjects(contexts: readonly ResolvedIdentityContext[], sessionSubjects: readonly AccountSubject[]): AccountSubject[] {
  const subjects = new Map<string, AccountSubject>();
  for (const context of contexts) {
    if (context.scope.productTrack !== 'A') continue;
    const existing = subjects.get(context.scope.taxSubjectId);
    if (!existing) {
      subjects.set(context.scope.taxSubjectId, subjectFromContext(context, sessionSubjects));
      continue;
    }
    subjects.set(context.scope.taxSubjectId, { ...existing,
      ...(context.scope.taxSubjectKind ? { tax_subject_kind: context.scope.taxSubjectKind } : {}),
      ...(context.scope.rail === 'tax_return' ? { engagement_id: context.scope.engagementId, context_id: context.contextId } : {}),
      context_ids: { ...existing.context_ids, [context.scope.rail]: context.contextId },
      resolved_contexts: [...(existing.resolved_contexts ?? []), context] });
  }
  return [...subjects.values()].filter((subject) => subject.context_ids?.tax_return);
}

function uniqueProductSubjects(contexts: readonly ResolvedIdentityContext[], sessionSubjects: readonly AccountSubject[],
  track: Exclude<ProductTrack, 'A'>): AccountSubject[] {
  const subjects = new Map<string, AccountSubject>();
  for (const context of contexts) {
    if (context.scope.productTrack !== track) continue;
    const current = subjects.get(context.scope.taxSubjectId);
    subjects.set(context.scope.taxSubjectId, current ? { ...current,
      ...(context.scope.taxSubjectKind ? { tax_subject_kind: context.scope.taxSubjectKind } : {}),
      ...(context.scope.rail === 'tax_return' ? { engagement_id: context.scope.engagementId, context_id: context.contextId } : {}),
      context_ids: { ...current.context_ids, [context.scope.rail]: context.contextId },
      resolved_contexts: [...(current.resolved_contexts ?? []), context],
    } : subjectFromContext(context, sessionSubjects));
  }
  return [...subjects.values()].filter((subject) => subject.context_ids?.tax_return);
}

function isIdentityContext(value: unknown): value is ResolvedIdentityContext {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ResolvedIdentityContext>;
  const scope = candidate.scope as Partial<ResolvedIdentityContext['scope']> | undefined;
  return typeof candidate.contextId === 'string' && typeof candidate.expiresAt === 'string'
    && typeof candidate.membershipId === 'string' && typeof candidate.actingCapacity === 'string'
    && !!scope && typeof scope.workspaceId === 'string' && typeof scope.taxSubjectId === 'string'
    && typeof scope.engagementId === 'string' && typeof scope.packageId === 'string' && typeof scope.periodId === 'string'
    && (scope.productTrack === 'A' || scope.productTrack === 'B1' || scope.productTrack === 'B2')
    && (scope.rail === 'tax_return' || scope.rail === 'e_invoice')
    && (scope.taxSubjectKind === undefined || scope.taxSubjectKind === 'organization' || scope.taxSubjectKind === 'individual');
}

function surfaceName(product: PersonaId): string {
  return product === 'practitioner' ? 'Tax practice' : product === 'business' ? 'Business tax' : 'Personal tax';
}
